// Цена сессии, правила отмены и сколько начислить по статусу.
import { GROUP_KIND, type GroupKind } from './groups.ts';
import { STATUS, type Status } from './status.ts';

export function hasValue<T>(v: T | null | undefined | ''): v is T {
  return v !== '' && v !== null && v !== undefined;
}

export interface Priced { price?: number | null; cur?: string | null }
export interface PricedGroup extends Priced { kind: GroupKind }
export type PriceFrom = 'client' | 'group' | 'type' | 'none';
export interface Price { price: number | null; cur: string; from: PriceFrom }

// Своя цена клиента всегда главная → цена группы (у коммерческого проекта — всегда его, даже пустая;
// у личной группы — если задана) → цена по умолчанию из типа сессии.
export function priceFor(client: Priced | null, group: PricedGroup | null, type: Priced | null,
  mainCur: string): Price {
  if (client && hasValue(client.price)) {
    return { price: Number(client.price) || 0, cur: client.cur || group?.cur || mainCur, from: 'client' };
  }
  if (group && (group.kind === GROUP_KIND.PROJECT || hasValue(group.price))) {
    return { price: hasValue(group.price) ? Number(group.price) || 0 : null, cur: group.cur || mainCur, from: 'group' };
  }
  if (type && hasValue(type.price)) {
    return { price: Number(type.price) || 0, cur: type.cur || client?.cur || mainCur, from: 'type' };
  }
  return { price: null, cur: client?.cur || mainCur, from: 'none' };
}

// Правило оплаты: '0%', '50%', '100%' или число (своя сумма). Пусто = 0.
export function applyRule(rule: string | number | null | undefined, price: number): number {
  const s = String(rule ?? '').trim().replace(',', '.');
  if (!s) return 0;
  if (/%$/.test(s)) {
    const p = parseFloat(s);
    return Number.isFinite(p) ? Math.round(price * p) / 100 : 0;
  }
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
}

export interface RuleDefaults { rule_hours: number | string; rule_late: string; rule_noshow: string; rule_psy: string }
export interface OwnRules { ruleHours?: number | null; ruleLate?: string | null; ruleNoShow?: string | null }
export interface Rules { hours: number; late: string; noshow: string; psy: string; source: string }

// Действующие правила: свои у клиента → у группы (если у группы заданы) → по умолчанию.
export function effectiveRules(client: (OwnRules & { ownRules?: boolean }) | null,
  group: (OwnRules & { name?: string }) | null, settings: RuleDefaults): Rules {
  let r: Rules = { hours: Number(settings.rule_hours) || 24, late: settings.rule_late, noshow: settings.rule_noshow,
    psy: settings.rule_psy, source: 'по умолчанию' };
  if (group && (hasValue(group.ruleHours) || hasValue(group.ruleLate) || hasValue(group.ruleNoShow))) {
    r = { hours: Number(group.ruleHours) || r.hours, late: hasValue(group.ruleLate) ? group.ruleLate : r.late,
      noshow: hasValue(group.ruleNoShow) ? group.ruleNoShow : r.noshow, psy: r.psy,
      source: 'группы ' + (group.name ?? '') };
  }
  if (client?.ownRules) {
    r = { hours: Number(client.ruleHours) || r.hours, late: hasValue(client.ruleLate) ? client.ruleLate : r.late,
      noshow: hasValue(client.ruleNoShow) ? client.ruleNoShow : r.noshow, psy: r.psy, source: 'свои' };
  }
  return r;
}

export function chargeFor(status: Status, price: number | null | undefined, rules: Rules): number {
  const p = Number(price) || 0;
  switch (status) {
    case STATUS.DONE: return p;
    case STATUS.CANCEL_LATE: return applyRule(rules.late, p);
    case STATUS.NO_SHOW: return applyRule(rules.noshow, p);
    case STATUS.CANCEL_PSY: return applyRule(rules.psy, p);
    default: return 0;
  }
}
